/**
 * Security utilities for the application
 */

/**
 * Content Security Policy configuration
 * Now implemented via meta tags in index.html
 */
export const CSP_DIRECTIVES = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'"], // Only for JSON-LD structured data
  'style-src': ["'self'", "'unsafe-inline'"],
  'img-src': ["'self'", "data:", "https:"],
  'font-src': ["'self'"],
  'connect-src': ["'self'", "https://uqjjisbrbasocuhmbviz.supabase.co"],
  'frame-ancestors': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"]
};

/**
 * Security headers configuration
 */
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()'
};

/**
 * Sanitize HTML content for safe rendering
 * Use this for any user-generated content that needs to be displayed as HTML
 */
export function sanitizeHTML(html: string): string {
  // For now, we strip all HTML tags as we don't need rich content
  // In the future, consider using a library like DOMPurify for more sophisticated sanitization
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .trim();
}

/**
 * Validate that JSON-LD structured data is safe for insertion
 * This is specifically for SEO structured data that we control
 */
export function validateStructuredData(data: any): boolean {
  try {
    const jsonString = JSON.stringify(data);
    
    // Check for potential XSS patterns
    const dangerousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i
    ];
    
    return !dangerousPatterns.some(pattern => pattern.test(jsonString));
  } catch {
    return false;
  }
}

/**
 * Enhanced rate limiting for form submissions (client-side protection)
 * Server-side rate limiting is the primary protection
 */
class EnhancedClientRateLimit {
  private attempts: Map<string, number[]> = new Map();
  private blockedUsers: Map<string, number> = new Map();
  private readonly maxAttempts = 3; // Reduced from 5
  private readonly windowMs = 15 * 60 * 1000; // 15 minutes
  private readonly blockDurationMs = 60 * 60 * 1000; // 1 hour

  isAllowed(identifier: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    
    // Check if user is temporarily blocked
    const blockUntil = this.blockedUsers.get(identifier);
    if (blockUntil && now < blockUntil) {
      return {
        allowed: false,
        message: "Trop de tentatives. Veuillez patienter avant de réessayer."
      };
    }
    
    // Remove expired block
    if (blockUntil && now >= blockUntil) {
      this.blockedUsers.delete(identifier);
    }
    
    const attempts = this.attempts.get(identifier) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      // Block user for 1 hour
      this.blockedUsers.set(identifier, now + this.blockDurationMs);
      return {
        allowed: false,
        message: "Limite de tentatives atteinte. Réessayez dans 1 heure."
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(identifier, recentAttempts);
    return { allowed: true };
  }

  // Clear attempts for successful submission
  clearAttempts(identifier: string): void {
    this.attempts.delete(identifier);
  }
}

export const contactFormRateLimit = new EnhancedClientRateLimit();